import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { FaTimes, FaAndroid, FaApple, FaGlobe, FaMobile } from 'react-icons/fa';
import { authenticatedFetch } from '../utils/tokenUtils';

const ShortLinkForm = ({ isOpen, onClose, onSubmit, shortLink = null, projects = [], isLoading = false }) => {
  const [formData, setFormData] = useState({
    projectId: '',
    shortCode: '',
    title: '',
    androidUrl: '',
    iosUrl: '',
    webUrl: ''
  });

  const [errors, setErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [platformUrls, setPlatformUrls] = useState({
    android: '',
    ios: '',
    web: ''
  });

  useEffect(() => {
    if (shortLink) {
      console.log('ShortLinkForm - shortLink received:', shortLink);
      
      // Handle both string ID and populated object for projectId
      let projectId = '';
      if (typeof shortLink.projectId === 'string') {
        projectId = shortLink.projectId;
      } else if (shortLink.projectId && shortLink.projectId._id) {
        projectId = shortLink.projectId._id;
      }
      
      const newFormData = {
        projectId: projectId,
        shortCode: shortLink.shortCode || '',
        title: shortLink.title || '',
        androidUrl: shortLink.androidUrl || '',
        iosUrl: shortLink.iosUrl || '',
        webUrl: shortLink.webUrl || shortLink.originalUrl || ''
      };
      
      console.log('ShortLinkForm - setting form data:', newFormData);
      setFormData(newFormData);
      
      // Fetch products for the selected project in edit mode
      if (projectId) {
        fetchProducts(projectId);
      }
    } else {
      setFormData({
        projectId: '',
        shortCode: '',
        title: '',
        androidUrl: '',
        iosUrl: '',
        webUrl: ''
      });
    }
    setErrors({});
    setProducts([]);
    setSelectedProduct(null);
    setPlatformUrls({ android: '', ios: '', web: '' });
  }, [shortLink, isOpen]);

  // Fetch products when project changes
  useEffect(() => {
    if (formData.projectId) {
      fetchProducts(formData.projectId);
    } else {
      setProducts([]);
      setSelectedProduct(null);
      setPlatformUrls({ android: '', ios: '', web: '' });
    }
  }, [formData.projectId]);

  const fetchProducts = async (projectId) => {
    try {
      const response = await authenticatedFetch(`/api/dashboard/user/projects/${projectId}/products`);
      const data = await response.json();
      setProducts(data.products || []);
      
      // Auto-fill platform URLs if products exist and we're not in edit mode
      if (data.products && data.products.length > 0 && !shortLink) {
        const urls = { android: '', ios: '', web: '' };
        
        data.products.forEach(product => {
          if (product.platform === 'android' && product.appStoreLinks?.googlePlay) {
            urls.android = product.appStoreLinks.googlePlay;
          } else if (product.platform === 'ios' && product.appStoreLinks?.appStore) {
            urls.ios = product.appStoreLinks.appStore;
          } else if (product.platform === 'web' && product.website) {
            urls.web = product.website;
          }
        });
        
        setPlatformUrls(urls);
        
        // Auto-fill form data with platform URLs only in create mode
        setFormData(prev => ({
          ...prev,
          androidUrl: urls.android,
          iosUrl: urls.ios,
          webUrl: urls.web || 'https://example.com' // Default web URL
        }));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'android':
        return <FaAndroid className="h-4 w-4 text-green-400" />;
      case 'ios':
        return <FaApple className="h-4 w-4 text-gray-300" />;
      case 'web':
        return <FaGlobe className="h-4 w-4 text-blue-400" />;
      default:
        return <FaMobile className="h-4 w-4 text-gray-400" />;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.webUrl.trim()) {
      newErrors.webUrl = 'Web URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.webUrl)) {
      newErrors.webUrl = 'Please enter a valid URL starting with http:// or https://';
    }

    if (!formData.projectId) {
      newErrors.projectId = 'Project selection is required';
    }

    if (formData.shortCode && (formData.shortCode.length < 4 || formData.shortCode.length > 50)) {
      newErrors.shortCode = 'Short code must be between 4-50 characters';
    } else if (formData.shortCode && !/^[a-zA-Z0-9_-]+$/.test(formData.shortCode)) {
      newErrors.shortCode = 'Short code can only contain letters, numbers, hyphens, and underscores';
    }

    if (formData.title && formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-gray-900 border border-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <DialogTitle as="h3" className="text-lg font-medium leading-6 text-white">
                    {shortLink ? 'Edit Short Link' : 'Create New Short Link'}
                  </DialogTitle>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <FaTimes className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Link Information Section */}
                  <div>
                    <h4 className="text-md font-medium text-white mb-4">Link Information</h4>
                    
                    <div className="space-y-4">
                      {/* Project Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Project *
                        </label>
                        <select
                          value={formData.projectId}
                          onChange={(e) => handleInputChange('projectId', e.target.value)}
                          disabled={!!shortLink}
                          className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                            errors.projectId ? 'border-red-500' : 'border-gray-700'
                          } ${!!shortLink ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <option value="">Select Project</option>
                          {projects.map((project) => (
                            <option key={project._id} value={project._id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                        {errors.projectId && (
                          <p className="mt-1 text-sm text-red-400">{errors.projectId}</p>
                        )}
                      </div>

                      {/* Platform URLs */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Platform URLs
                        </label>
                        <div className="space-y-3">
                          {/* Web URL (Default) */}
                          <div>
                            <label className="flex items-center space-x-2 text-sm text-gray-300 mb-1">
                              <FaGlobe className="h-4 w-4 text-blue-400" />
                              <span>Web URL (Default) *</span>
                            </label>
                            <input
                              type="url"
                              value={formData.webUrl}
                              onChange={(e) => handleInputChange('webUrl', e.target.value)}
                              className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                errors.webUrl ? 'border-red-500' : 'border-gray-700'
                              }`}
                              placeholder="https://example.com"
                            />
                            {errors.webUrl && (
                              <p className="mt-1 text-sm text-red-400">{errors.webUrl}</p>
                            )}
                          </div>

                          {/* Android URL */}
                          <div>
                            <label className="flex items-center space-x-2 text-sm text-gray-300 mb-1">
                              <FaAndroid className="h-4 w-4 text-green-400" />
                              <span>Android URL</span>
                            </label>
                            <input
                              type="url"
                              value={formData.androidUrl}
                              onChange={(e) => handleInputChange('androidUrl', e.target.value)}
                              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="https://play.google.com/store/apps/details?id=..."
                            />
                          </div>

                          {/* iOS URL */}
                          <div>
                            <label className="flex items-center space-x-2 text-sm text-gray-300 mb-1">
                              <FaApple className="h-4 w-4 text-gray-300" />
                              <span>iOS URL</span>
                            </label>
                            <input
                              type="url"
                              value={formData.iosUrl}
                              onChange={(e) => handleInputChange('iosUrl', e.target.value)}
                              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="https://apps.apple.com/app/..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* Custom Short Code */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Custom Short Code {shortLink ? '(Read Only)' : '(Optional)'}
                        </label>
                        <input
                          type="text"
                          value={formData.shortCode}
                          onChange={(e) => handleInputChange('shortCode', e.target.value)}
                          disabled={!!shortLink}
                          className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                            errors.shortCode ? 'border-red-500' : 'border-gray-700'
                          } ${shortLink ? 'opacity-50 cursor-not-allowed' : ''}`}
                          placeholder="Appacts"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          Leave empty to auto-generate. Must be 4-50 characters, letters, numbers, hyphens, and underscores only.
                        </p>
                        {errors.shortCode && (
                          <p className="mt-1 text-sm text-red-400">{errors.shortCode}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Information Section */}
                  <div>
                    <h4 className="text-md font-medium text-white mb-4">Additional Information</h4>
                    
                    <div className="space-y-4">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Title
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                            errors.title ? 'border-red-500' : 'border-gray-700'
                          }`}
                          placeholder="Short description of the link"
                          maxLength={100}
                        />
                        {errors.title && (
                          <p className="mt-1 text-sm text-red-400">{errors.title}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-800">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isLoading ? 'Saving...' : (shortLink ? 'Update Short Link' : 'Create Short Link')}
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ShortLinkForm;